import React, { useEffect, useState } from 'react';
import { Card, Button, Form, Row, Col, Table, Badge, Modal, Alert, Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import adminService from '../../services/adminService';

interface BookingFeeTier {
  id: number;
  min_price: number;
  max_price: number;
  booking_fee: number;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

const BookingFeeTiers: React.FC = () => {
  const [tiers, setTiers] = useState<BookingFeeTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingTier, setEditingTier] = useState<BookingFeeTier | null>(null);
  const [formData, setFormData] = useState({
    min_price: 0,
    max_price: 0,
    booking_fee: 0,
    is_active: true,
    display_order: 0,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      setLoading(true);
      const response = await adminService.getBookingFeeTiers(false);
      if (response.success && response.data) {
        // Parse numeric values from database (PostgreSQL DECIMAL returns as strings)
        const parsedTiers = response.data.map((tier: any) => ({
          ...tier,
          min_price: parseFloat(tier.min_price) || 0,
          max_price: parseFloat(tier.max_price) || 0,
          booking_fee: parseFloat(tier.booking_fee) || 0,
          display_order: parseInt(tier.display_order) || 0,
        }));
        setTiers(parsedTiers);
      } else {
        toast.error(response.error || 'Failed to load booking fee tiers');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load booking fee tiers');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.min_price < 0) {
      newErrors.min_price = 'Minimum price must be >= 0';
    }

    if (formData.max_price <= formData.min_price) {
      newErrors.max_price = 'Maximum price must be greater than minimum price';
    }

    if (formData.booking_fee < 0) {
      newErrors.booking_fee = 'Booking fee must be >= 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenModal = (tier?: BookingFeeTier) => {
    if (tier) {
      setEditingTier(tier);
      setFormData({
        min_price: typeof tier.min_price === 'string' ? parseFloat(tier.min_price) : tier.min_price,
        max_price: typeof tier.max_price === 'string' ? parseFloat(tier.max_price) : tier.max_price,
        booking_fee: typeof tier.booking_fee === 'string' ? parseFloat(tier.booking_fee) : tier.booking_fee,
        is_active: tier.is_active,
        display_order: typeof tier.display_order === 'string' ? parseInt(tier.display_order) : tier.display_order,
      });
    } else {
      setEditingTier(null);
      setFormData({
        min_price: 0,
        max_price: 0,
        booking_fee: 0,
        is_active: true,
        display_order: tiers.length,
      });
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTier(null);
    setFormData({
      min_price: 0,
      max_price: 0,
      booking_fee: 0,
      is_active: true,
      display_order: 0,
    });
    setErrors({});
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      if (editingTier) {
        const response = await adminService.updateBookingFeeTier(editingTier.id, formData);
        if (response.success) {
          toast.success('Booking fee tier updated successfully');
          fetchTiers();
          handleCloseModal();
        } else {
          toast.error(response.error || 'Failed to update booking fee tier');
        }
      } else {
        const response = await adminService.createBookingFeeTier(formData);
        if (response.success) {
          toast.success('Booking fee tier created successfully');
          fetchTiers();
          handleCloseModal();
        } else {
          toast.error(response.error || 'Failed to create booking fee tier');
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save booking fee tier');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this booking fee tier?')) {
      return;
    }

    try {
      const response = await adminService.deleteBookingFeeTier(id);
      if (response.success) {
        toast.success('Booking fee tier deleted successfully');
        fetchTiers();
      } else {
        toast.error(response.error || 'Failed to delete booking fee tier');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete booking fee tier');
    }
  };

  const handleToggleActive = async (tier: BookingFeeTier) => {
    try {
      const response = await adminService.updateBookingFeeTier(tier.id, {
        ...tier,
        is_active: !tier.is_active,
      });
      if (response.success) {
        toast.success(`Booking fee tier ${!tier.is_active ? 'activated' : 'deactivated'} successfully`);
        fetchTiers();
      } else {
        toast.error(response.error || 'Failed to update booking fee tier');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update booking fee tier');
    }
  };

  return (
    <>
      <Helmet>
        <title>Booking Fee Tiers - MyCinemas Admin</title>
      </Helmet>

      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Booking Fee Tiers</h2>
          <Button variant="primary" onClick={() => handleOpenModal()}>
            <i className="bi bi-plus-circle me-2"></i>
            Add New Tier
          </Button>
        </div>

        <Card>
          <Card.Body>
            <Alert variant="info" className="mb-4">
              <strong>How it works:</strong> Booking fees are calculated based on ticket price ranges. 
              When a customer books tickets, the system finds the appropriate tier based on the total ticket price 
              and applies the corresponding booking fee. Make sure price ranges don't overlap for active tiers.
            </Alert>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading booking fee tiers...</p>
              </div>
            ) : tiers.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">No booking fee tiers found. Create your first tier to get started.</p>
              </div>
            ) : (
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Price Range</th>
                    <th>Booking Fee</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers
                    .sort((a, b) => {
                      const orderA = typeof a.display_order === 'number' ? a.display_order : parseInt(a.display_order) || 0;
                      const orderB = typeof b.display_order === 'number' ? b.display_order : parseInt(b.display_order) || 0;
                      const priceA = typeof a.min_price === 'number' ? a.min_price : parseFloat(a.min_price) || 0;
                      const priceB = typeof b.min_price === 'number' ? b.min_price : parseFloat(b.min_price) || 0;
                      return orderA - orderB || priceA - priceB;
                    })
                    .map((tier) => {
                      const minPrice = typeof tier.min_price === 'number' ? tier.min_price : parseFloat(tier.min_price) || 0;
                      const maxPrice = typeof tier.max_price === 'number' ? tier.max_price : parseFloat(tier.max_price) || 0;
                      const bookingFee = typeof tier.booking_fee === 'number' ? tier.booking_fee : parseFloat(tier.booking_fee) || 0;
                      return (
                      <tr key={tier.id}>
                        <td>{tier.display_order}</td>
                        <td>
                          ₹{minPrice.toFixed(2)} - ₹{maxPrice.toFixed(2)}
                        </td>
                        <td>₹{bookingFee.toFixed(2)}</td>
                        <td>
                          <Badge bg={tier.is_active ? 'success' : 'secondary'}>
                            {tier.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => handleOpenModal(tier)}
                            className="me-2"
                          >
                            <i className="bi bi-pencil"></i> Edit
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => handleToggleActive(tier)}
                            className="me-2"
                          >
                            <i className={`bi bi-toggle-${tier.is_active ? 'on' : 'off'}`}></i>{' '}
                            {tier.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger"
                            onClick={() => handleDelete(tier.id)}
                          >
                            <i className="bi bi-trash"></i> Delete
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingTier ? 'Edit Booking Fee Tier' : 'Add New Booking Fee Tier'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Minimum Price (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.min_price}
                    onChange={(e) => handleInputChange('min_price', parseFloat(e.target.value) || 0)}
                    isInvalid={!!errors.min_price}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.min_price}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Minimum ticket price for this tier (inclusive)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Maximum Price (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.max_price}
                    onChange={(e) => handleInputChange('max_price', parseFloat(e.target.value) || 0)}
                    isInvalid={!!errors.max_price}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.max_price}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Maximum ticket price for this tier (inclusive)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Booking Fee (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.booking_fee}
                    onChange={(e) => handleInputChange('booking_fee', parseFloat(e.target.value) || 0)}
                    isInvalid={!!errors.booking_fee}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.booking_fee}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Booking fee amount for tickets in this price range
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={formData.display_order}
                    onChange={(e) => handleInputChange('display_order', parseInt(e.target.value) || 0)}
                  />
                  <Form.Text className="text-muted">
                    Order for displaying tiers (lower numbers appear first)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is_active"
                label="Active"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
              />
              <Form.Text className="text-muted">
                Only active tiers are used for booking fee calculation
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BookingFeeTiers;

